import CacheStorage from "./CacheStorage";
import {
  CacheProjectStatus,
  ICacheModel,
  ICacheStorage,
  ICacheStorageState,
  ProjectResourceInfo,
} from "../types";

class CacheModel implements ICacheModel {
  private store!: ICacheStorage;

  private state!: ICacheStorageState;

  private _isLoaded = false;

  private readonly defaultState: ICacheStorageState = {
    status: CacheProjectStatus.UNCACHED,
    lastUpdated: null,
    keys: [],
    resources: [],
  };

  constructor(public readonly id: string, defaultState?: ICacheStorageState) {
    this.store = new CacheStorage(id);
    if (defaultState) {
      this.state = Object.assign({}, this.defaultState, defaultState);
    } else {
      this.setDefaultState();
    }
  }

  public getState() {
    return structuredClone(this.state);
  }

  async reset() {
    this.setDefaultState();
    await this.save();
  }

  protected setDefaultState() {
    this.state = { ...this.defaultState };
  }

  isStopping(): boolean {
    return this.state.status === CacheProjectStatus.STOPPING;
  }

  isCancel(): boolean {
    return this.state.status !== CacheProjectStatus.PROCESSING;
  }

  isPaused(): boolean {
    return this.state.status === CacheProjectStatus.PAUSED;
  }

  isFinished(): boolean {
    return this.state.status === CacheProjectStatus.FINISHED;
  }

  isProcessing(): boolean {
    return this.state.status === CacheProjectStatus.PROCESSING;
  }

  isClearing(): boolean {
    return this.state.status === CacheProjectStatus.IS_CLEARING;
  }

  isRunning(): boolean {
    return [
      CacheProjectStatus.PROCESSING,
      CacheProjectStatus.IS_CLEARING,
      CacheProjectStatus.STOPPING,
    ].includes(this.state.status);
  }

  getStatus(): CacheProjectStatus {
    return this.state.status;
  }

  setStatus(status: CacheProjectStatus): void {
    this.state.status = status;
    if (this.state.status === CacheProjectStatus.FINISHED) {
      this.state.lastUpdated = Date.now();
    } else {
      this.state.lastUpdated = null;
    }
  }

  isCached(key: string): boolean {
    return this.state.keys.includes(key);
  }

  setKeyCached(key: string): void {
    if (this.state.keys.includes(key)) return;
    this.state.keys.push(key);
  }

  async load(): Promise<void> {
    const result = await this.store.get();
    this._isLoaded = true;
    if (!result) return;
    this.state = result;
    if (this.isProcessing()) {
      this.setStatus(CacheProjectStatus.PAUSED);
      await this.save();
    }
  }

  get isLoaded(): boolean {
    return this._isLoaded;
  }

  getKeysCached(): string[] {
    return Object.keys(this.state.keys);
  }

  getKeys() {
    return structuredClone(this.state.keys);
  }

  getResources() {
    return structuredClone(this.state.resources);
  }

  setResources(resources: ProjectResourceInfo[]) {
    this.state.resources = resources;
  }

  setKeys(keys: string[]) {
    this.state.keys = keys;
  }

  getLastUpdated(): number | null {
    return this.state.lastUpdated;
  }

  save(): Promise<void> {
    return this.store.save(this.state);
  }
}

export default CacheModel;
